import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Building2,
  Check,
  ChevronRight,
  ChevronsUpDown,
  Ellipsis,
  Eye,
  GitMerge,
  List,
  ListTree,
  PenLine,
  Phone,
  Plus,
  Trash2,
} from 'lucide-react';
import { DashboardLayout } from '../components/dashboard';
import { HierarchicalOrganizationTable } from '../components/organizations/HierarchicalOrganizationTable';
import { organizationService } from '../services/organizationService';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { MessageModal } from '../components/common/MessageModal';
import { Pagination } from '../components/common/Pagination';
import { SelectionActionBar } from '../components/common/SelectionActionBar';
import { MergeResolutionSheet, MergeFieldGroup } from '../components/common/MergeResolutionSheet';
import { Organization, OrganizationHierarchy } from '../types/organization';
import { useDebounce } from '../hooks/useDebounce';
import { fuzzyFilter, highlightMatch } from '../utils/fuzzySearch';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common';
import { canDeleteOrganization, canUpdateOrganization, canMergeOrganizations } from '../utils/permissions';
import { useTaxonomyColors } from '../hooks/useTaxonomyColors';
import { foldText } from '../utils/fuzzySearch';
import {
  Avatar,
  Button,
  Card,
  CategoryPill,
  Checkbox,
  EmptyState,
  IconButton,
  Menu,
  MenuItem,
  Page,
  PageHeader,
  Pill,
  SearchInput,
  SegmentedControl,
  Select,
  Skeleton,
  Swatch,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  statusTone,
} from '../components/ui';

type SortField = 'name' | 'type' | 'status' | 'createdAt';
type SortOrder = 'ASC' | 'DESC';
type ViewMode = 'table' | 'hierarchy';

const DEFAULT_LIMIT = 25;
/** Floats the pagination above the selection bar (its 48px pill and 24px margin) */
const SELECTION_BAR_CLEARANCE = 84;

const STATUS_OPTIONS = [
  { value: '', label: 'Status' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'draft', label: 'Draft' },
  { value: 'rejected', label: 'Rejected' },
];

const ORG_MERGE_FIELDS: MergeFieldGroup[] = [
  {
    title: 'Basics',
    icon: Building2,
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'description', label: 'Description' },
    ],
  },
  {
    title: 'Contact',
    icon: Phone,
    fields: [
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'address', label: 'Address' },
      { key: 'website', label: 'Website' },
    ],
  },
];

/** "religious-organization" → "Religious organization" */
const typeLabel = (type?: string) => {
  if (!type) return '';
  // Only reformat slugs; labels such as "Non-Governmental Organization (NGO)" stay as they are
  if (!/^[a-z0-9]+([-_][a-z0-9]+)*$/.test(type)) return type;
  const text = type.replace(/[-_]+/g, ' ');
  // Short single words are acronyms: "ngo" → "NGO"
  if (!text.includes(' ') && text.length <= 4) return text.toUpperCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const statusBadge = (status: string) => (
  <Pill tone={statusTone(status)}>{status.charAt(0).toUpperCase() + status.slice(1)}</Pill>
);

/** Distinct people assigned, when the list API includes assignments */
const peopleCount = (org: Organization): number | null => {
  const assignments = (org as any).assignments;
  if (!Array.isArray(assignments)) return null;
  return new Set(assignments.map((a: any) => a.stakeholderId ?? a.stakeholder?.id ?? a.id)).size;
};

const HighlightedText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const segments = highlightMatch(text, query);
  return (
    <>
      {segments.map((seg, i) =>
        seg.highlight ? (
          <mark key={i} className="rounded-[3px] bg-primary-50 text-primary-700">
            {seg.text}
          </mark>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
};

/** Organizations read as teal rounded squares; a logo replaces the initials */
const OrgMark: React.FC<{ org: Organization }> = ({ org }) => (
  <Avatar
    kind="organization"
    size="xs"
    name={org.name}
    image={org.logoUrl ? <img src={org.logoUrl} alt="" className="h-full w-full object-cover" /> : undefined}
  />
);

const SkeletonRow: React.FC<{ columns: number }> = ({ columns }) => (
  <tr>
    <td className="h-11 px-4">
      <div className="flex items-center gap-2.5">
        <Skeleton className="h-6 w-6 rounded-lg" />
        <Skeleton className="h-3.5 w-40" />
      </div>
    </td>
    {Array.from({ length: columns - 1 }).map((_, i) => (
      <td key={i} className="h-11 px-4">
        <Skeleton className="h-3.5 w-16" />
      </td>
    ))}
  </tr>
);

const SkeletonListRow: React.FC = () => (
  <li className="flex min-h-[56px] items-center gap-3 px-4">
    <Skeleton className="h-6 w-6 rounded-lg" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-3.5 w-40" />
      <Skeleton className="h-3 w-24" />
    </div>
  </li>
);

const SortHeader: React.FC<{
  label: string;
  field: SortField;
  currentSort: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
}> = ({ label, field, currentSort, currentOrder, onSort, className = '' }) => {
  const isActive = currentSort === field;
  const Icon = !isActive ? ChevronsUpDown : currentOrder === 'ASC' ? ArrowUp : ArrowDown;
  return (
    <TH
      scope="col"
      className={className}
      aria-sort={isActive ? (currentOrder === 'ASC' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`group -mx-1 inline-flex h-7 items-center gap-1 rounded-md px-1 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
          isActive ? 'text-ink' : ''
        }`}
      >
        {label}
        <Icon
          className={`h-3 w-3 ${isActive ? 'text-primary-600' : 'text-neutral-300 opacity-0 group-hover:opacity-100'}`}
          strokeWidth={2}
        />
      </button>
    </TH>
  );
};

export const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { colorOf } = useTaxonomyColors();

  // URL-driven state
  const initialSearch = searchParams.get('q') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const initialSortBy = (searchParams.get('sortBy') as SortField) || 'name';
  const initialSortOrder = (searchParams.get('sortOrder') as SortOrder) || 'ASC';
  const initialStatus = searchParams.get('status') || '';
  const initialType = searchParams.get('type') || '';
  const initialView = (searchParams.get('view') as ViewMode) || 'table';

  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [sortBy, setSortBy] = useState<SortField>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);

  // Data
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hierarchy data (only loaded when in hierarchy view)
  const [hierarchyData, setHierarchyData] = useState<OrganizationHierarchy[]>([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title: string; message: React.ReactNode }>({
    open: false, title: '', message: ''
  });

  // Merge selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [mergeSheetOpen, setMergeSheetOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<Organization | null>(null);
  const [mergeSource, setMergeSource] = useState<Organization | null>(null);
  const [merging, setMerging] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (page > 1) params.set('page', String(page));
    if (limit !== DEFAULT_LIMIT) params.set('limit', String(limit));
    if (sortBy !== 'name') params.set('sortBy', sortBy);
    if (sortOrder !== 'ASC') params.set('sortOrder', sortOrder);
    if (statusFilter) params.set('status', statusFilter);
    if (typeFilter) params.set('type', typeFilter);
    if (viewMode !== 'table') params.set('view', viewMode);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, page, limit, sortBy, sortOrder, statusFilter, typeFilter, viewMode, setSearchParams]);

  // Load flat table data
  const loadOrganizations = useCallback(async () => {
    if (viewMode !== 'table') return;
    try {
      setLoading(true);
      setError(null);
      const result = await organizationService.getOrganizations({
        search: debouncedSearch || undefined,
        page,
        limit,
        sortBy,
        sortOrder,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      });
      setOrganizations(result.data);
      setTotal(result.total);
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to load organizations';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortBy, sortOrder, statusFilter, typeFilter, viewMode]);

  // Load hierarchy data
  const loadHierarchy = useCallback(async () => {
    if (viewMode !== 'hierarchy') return;
    try {
      setHierarchyLoading(true);
      const data = await organizationService.getOrganizationHierarchy();
      setHierarchyData(data || []);
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to load organizations';
      setError(msg);
    } finally {
      setHierarchyLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, typeFilter, limit]);

  // Clear selection on page/view change
  useEffect(() => {
    setSelectedIds([]);
  }, [page, debouncedSearch, statusFilter, typeFilter]);

  // Exit selection mode when switching to hierarchy view
  useEffect(() => {
    if (viewMode !== 'table') {
      setSelectionMode(false);
      setSelectedIds([]);
    }
  }, [viewMode]);

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  // Fuzzy-highlight the current page items
  const highlightedOrgs = useMemo(() => {
    if (!debouncedSearch) return organizations.map(org => ({ item: org, score: 1 }));
    return fuzzyFilter(organizations, debouncedSearch, (org) => [
      org.name, org.type || '', org.description || '',
    ]);
  }, [organizations, debouncedSearch]);

  // Hierarchy filtering
  const filteredHierarchy = useMemo<OrganizationHierarchy[]>(() => {
    if (viewMode !== 'hierarchy') return [];
    const q = foldText(search);
    const matchNode = (n: OrganizationHierarchy) => {
      const matchesSearch = !q || foldText(n.name).includes(q) || foldText(n.type).includes(q);
      const matchesType = !typeFilter || n.type === typeFilter;
      return matchesSearch && matchesType;
    };
    const filterTree = (nodes: OrganizationHierarchy[]): OrganizationHierarchy[] => {
      return nodes.map(n => ({ ...n, children: filterTree(n.children || []) }))
        .map(n => {
          const selfMatch = matchNode(n);
          const childMatches = (n.children || []).length > 0;
          if (selfMatch || childMatches) return n;
          return null as any;
        })
        .filter(Boolean) as OrganizationHierarchy[];
    };
    return filterTree(hierarchyData);
  }, [hierarchyData, search, typeFilter, viewMode]);

  // Build unique types from hierarchy data (for filter dropdown)
  const allTypes = useMemo(() => {
    const set = new Set<string>();
    if (viewMode === 'hierarchy') {
      const walk = (nodes: OrganizationHierarchy[]) => {
        nodes.forEach(n => {
          if (n.type) set.add(n.type);
          if (n.children?.length) walk(n.children);
        });
      };
      walk(hierarchyData);
    } else {
      organizations.forEach(o => { if (o.type) set.add(o.type); });
    }
    return Array.from(set).sort();
  }, [organizations, hierarchyData, viewMode]);

  // Hierarchy pagination (client-side)
  const paginatedHierarchy = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredHierarchy.slice(start, start + limit);
  }, [filteredHierarchy, page, limit]);

  // Every node in the filtered tree, for the header count
  const hierarchyNodeCount = useMemo(() => {
    const count = (nodes: OrganizationHierarchy[]): number =>
      nodes.reduce((sum, n) => sum + 1 + count(n.children || []), 0);
    return count(filteredHierarchy);
  }, [filteredHierarchy]);

  // Delete handler
  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) return;
    try {
      setDeletingId(confirmDeleteId);
      await organizationService.deleteOrganization(confirmDeleteId);
      if (viewMode === 'table') {
        await loadOrganizations();
      } else {
        await loadHierarchy();
      }
    } catch (err: any) {
      let errorMessage = 'Failed to delete organization';
      if (err.response?.data?.error?.message) errorMessage = err.response.data.error.message;
      else if (err.response?.data?.message) errorMessage = err.response.data.message;

      let detailedMessage: React.ReactNode = errorMessage;
      let modalTitle = "Can't delete";

      if (errorMessage.toLowerCase().includes('child organization')) {
        detailedMessage = (
          <div className="space-y-1.5">
            <p>It has sub-organizations.</p>
            <p className="text-neutral-500">Delete them or move them to another parent first.</p>
          </div>
        );
      } else if (errorMessage.toLowerCase().includes('approved')) {
        detailedMessage = (
          <div className="space-y-1.5">
            <p>It's approved.</p>
            <p className="text-neutral-500">Approved organizations need extra permission to delete.</p>
          </div>
        );
      } else if (errorMessage.toLowerCase().includes('permission') || errorMessage.toLowerCase().includes('forbidden')) {
        detailedMessage = (
          <div className="space-y-1.5">
            <p>You don't have permission.</p>
            <p className="text-neutral-500">Ask an administrator.</p>
          </div>
        );
      } else {
        detailedMessage = (
          <div className="space-y-1.5">
            <p>The organization couldn't be deleted.</p>
            <p className="text-neutral-500">{errorMessage}</p>
          </div>
        );
      }

      setErrorModal({ open: true, title: modalTitle, message: detailedMessage });
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  // ── Selection mode handlers ──

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedIds([]);
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      if (prev.length >= 2) {
        return [prev[1], id];
      }
      return [...prev, id];
    });
  };

  const handleRowClick = (orgId: string) => {
    if (selectionMode) {
      toggleSelection(orgId);
    } else {
      navigate(`/organizations/${orgId}`);
    }
  };

  // ── Merge handlers ──

  const handleOpenMerge = () => {
    const first = organizations.find(o => o.id === selectedIds[0]);
    const second = organizations.find(o => o.id === selectedIds[1]);
    if (!first || !second) return;
    setMergeTarget(first);
    setMergeSource(second);
    setMergeSheetOpen(true);
  };

  const handleSwapMerge = () => {
    setMergeTarget(mergeSource);
    setMergeSource(mergeTarget);
  };

  const handleConfirmMerge = async (resolvedFields: Record<string, any>) => {
    if (!mergeSource || !mergeTarget) return;
    setMerging(true);
    try {
      await organizationService.mergeOrganizations(mergeSource.id, mergeTarget.id, resolvedFields);
      showSuccess('Merge Complete', `${mergeSource.name} has been merged into ${mergeTarget.name}.`);
      setMergeSheetOpen(false);
      setSelectionMode(false);
      setSelectedIds([]);
      await loadOrganizations();
      // Also refresh hierarchy data in case user switches views
      if (viewMode === 'hierarchy') {
        await loadHierarchy();
      }
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to merge organizations.';
      showError('Merge Failed', msg);
    } finally {
      setMerging(false);
    }
  };

  const isTableView = viewMode === 'table';
  const isLoading = isTableView ? loading : hierarchyLoading;
  const isEmpty = isTableView ? organizations.length === 0 : filteredHierarchy.length === 0;
  const totalForPagination = isTableView ? total : filteredHierarchy.length;
  const showMergeButton = canMergeOrganizations(user) && isTableView;
  const hasFilters = !!(search || statusFilter || typeFilter);

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setStatusFilter('');
  };

  const rowMenuItems = (org: Organization): MenuItem[] => {
    const items: MenuItem[] = [{ label: 'View', icon: Eye, onSelect: () => navigate(`/organizations/${org.id}`) }];
    if (canUpdateOrganization(user, org)) {
      items.push({ label: 'Edit', icon: PenLine, onSelect: () => navigate(`/organizations/${org.id}/edit`) });
    }
    if (canDeleteOrganization(user, org)) {
      items.push({
        label: deletingId === org.id ? 'Deleting' : 'Delete',
        icon: Trash2,
        danger: true,
        disabled: deletingId === org.id,
        onSelect: () => setConfirmDeleteId(org.id),
      });
    }
    return items;
  };

  const countLabel = (n: number) => `${n} organization${n === 1 ? '' : 's'}`;
  const meta = selectionMode
    ? 'Select 2 to merge'
    : isLoading
      ? ' '
      : countLabel(isTableView ? total : hierarchyNodeCount);

  const emptyState = (
    <EmptyState
      icon={Building2}
      title={hasFilters ? 'No matches' : 'No organizations'}
      action={
        hasFilters ? (
          <Button size="sm" variant="secondary" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : (
          <Button size="sm" variant="primary" onClick={() => navigate('/organizations/new')}>
            <Plus className="h-4 w-4" strokeWidth={2} />
            Add
          </Button>
        )
      }
    />
  );

  return (
    <DashboardLayout>
      <Page>
        <PageHeader
          title="Organizations"
          meta={meta}
          actions={
            <>
              {showMergeButton && (
                <Button size="sm" variant="secondary" onClick={toggleSelectionMode}>
                  <GitMerge className="h-4 w-4" strokeWidth={1.75} />
                  {selectionMode ? 'Cancel' : 'Merge'}
                </Button>
              )}
              <Button size="sm" variant="primary" onClick={() => navigate('/organizations/new')}>
                <Plus className="h-4 w-4" strokeWidth={2} />
                Add
              </Button>
            </>
          }
        />

        {/* Search & filters */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SearchInput
            className="w-full sm:w-72"
            placeholder="Search"
            aria-label="Search organizations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            aria-label="Type"
            className="min-w-0 flex-1 sm:w-44 sm:flex-none"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Type</option>
            {allTypes.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
          </Select>
          {isTableView && (
            <Select
              aria-label="Status"
              className="min-w-0 flex-1 sm:w-36 sm:flex-none"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </Select>
          )}
          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} className="hidden sm:inline-flex">
              Clear
            </Button>
          )}
          <SegmentedControl
            label="View"
            className="flex-shrink-0 sm:ml-auto"
            value={viewMode}
            onChange={setViewMode}
            items={[
              {
                value: 'table',
                title: 'List',
                label: (
                  <>
                    <List className="h-4 w-4" strokeWidth={1.75} />
                    <span className="hidden sm:inline">List</span>
                  </>
                ),
              },
              {
                value: 'hierarchy',
                title: 'Tree',
                label: (
                  <>
                    <ListTree className="h-4 w-4" strokeWidth={1.75} />
                    <span className="hidden sm:inline">Tree</span>
                  </>
                ),
              },
            ]}
          />
        </div>

        {/* Error */}
        {error && !isLoading && (
          <p role="alert" className="mb-3 flex flex-wrap items-center gap-x-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
            <button
              type="button"
              onClick={() => { setError(null); isTableView ? loadOrganizations() : loadHierarchy(); }}
              className="font-medium underline underline-offset-2"
            >
              Retry
            </button>
          </p>
        )}

        {/* List view */}
        {isTableView && (
          <>
            {/* Desktop */}
            <Card className="hidden sm:block">
              <div className="overflow-x-auto">
                <Table className="table-fixed">
                  <THead>
                    <tr>
                      {selectionMode && (
                        <TH scope="col" className="w-10 pr-0">
                          <span className="sr-only">Select</span>
                        </TH>
                      )}
                      <SortHeader label="Name" field="name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <SortHeader
                        label="Type"
                        field="type"
                        currentSort={sortBy}
                        currentOrder={sortOrder}
                        onSort={handleSort}
                        className="w-[24%] lg:w-[18%]"
                      />
                      <TH scope="col" className="hidden w-[15%] xl:table-cell">Parent</TH>
                      <TH scope="col" className="hidden w-[64px] text-right lg:table-cell">People</TH>
                      <SortHeader
                        label="Status"
                        field="status"
                        currentSort={sortBy}
                        currentOrder={sortOrder}
                        onSort={handleSort}
                        className="w-[96px]"
                      />
                      <SortHeader
                        label="Created"
                        field="createdAt"
                        currentSort={sortBy}
                        currentOrder={sortOrder}
                        onSort={handleSort}
                        className="hidden w-[96px] md:table-cell"
                      />
                      {!selectionMode && (
                        <TH scope="col" className="w-[56px]">
                          <span className="sr-only">Actions</span>
                        </TH>
                      )}
                    </tr>
                  </THead>
                  <TBody>
                    {isLoading ? (
                      Array.from({ length: Math.min(limit, 10) }).map((_, i) => <SkeletonRow key={i} columns={5} />)
                    ) : isEmpty ? (
                      <tr>
                        <td colSpan={8}>{emptyState}</td>
                      </tr>
                    ) : (
                      highlightedOrgs.map(({ item: org }) => {
                        const isSelected = selectedIds.includes(org.id);
                        const people = peopleCount(org);
                        return (
                          <TR
                            key={org.id}
                            interactive
                            aria-selected={selectionMode ? isSelected : undefined}
                            className={isSelected ? 'bg-primary-50/60 hover:bg-primary-50' : ''}
                            onClick={() => handleRowClick(org.id)}
                          >
                            {selectionMode && (
                              <TD className="pr-0" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => toggleSelection(org.id)}
                                  aria-label={`Select ${org.name}`}
                                />
                              </TD>
                            )}
                            <TD>
                              <div className="flex min-w-0 items-center gap-2.5">
                                <OrgMark org={org} />
                                <span className="truncate">
                                  <HighlightedText text={org.name} query={debouncedSearch} />
                                </span>
                                {org.endedOn && <DefunctPill />}
                              </div>
                            </TD>
                            <TD>
                              {org.type ? (
                                <CategoryPill
                                  title={org.type}
                                  color={colorOf('organization_type', org.type)}
                                  label={<HighlightedText text={typeLabel(org.type)} query={debouncedSearch} />}
                                />
                              ) : (
                                <span className="text-neutral-300">—</span>
                              )}
                            </TD>
                            <TD className="hidden truncate text-neutral-500 xl:table-cell" title={org.parent?.name}>
                              {org.parent?.name || <span className="text-neutral-300">—</span>}
                            </TD>
                            <TD className="hidden text-right tabular-nums text-neutral-500 lg:table-cell">
                              {people ? people : <span className="text-neutral-300">—</span>}
                            </TD>
                            <TD>{statusBadge(org.status)}</TD>
                            <TD className="hidden whitespace-nowrap text-[12px] tabular-nums text-neutral-500 md:table-cell">
                              {new Date(org.createdAt).toLocaleDateString()}
                            </TD>
                            {!selectionMode && (
                              <TD className="pr-2 text-right">
                                <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                                  <Menu
                                    items={rowMenuItems(org)}
                                    trigger={(props) => (
                                      <IconButton label={`Actions for ${org.name}`} size="sm" className="text-neutral-400" {...props}>
                                        <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                                      </IconButton>
                                    )}
                                  />
                                </div>
                              </TD>
                            )}
                          </TR>
                        );
                      })
                    )}
                  </TBody>
                </Table>
              </div>
            </Card>

            {/* Phones */}
            <Card className="sm:hidden">
              {isLoading ? (
                <ul className="divide-y divide-neutral-200/70">
                  {Array.from({ length: 6 }).map((_, i) => <SkeletonListRow key={i} />)}
                </ul>
              ) : isEmpty ? (
                emptyState
              ) : (
                <ul className="divide-y divide-neutral-200/70">
                  {highlightedOrgs.map(({ item: org }) => {
                    const isSelected = selectedIds.includes(org.id);
                    const subtitle = [typeLabel(org.type), org.parent?.name].filter(Boolean).join(' · ');
                    const typeColor = colorOf('organization_type', org.type);
                    return (
                      <li
                        key={org.id}
                        className={`flex min-h-[56px] items-center pr-1.5 transition-colors first:rounded-t-2xl last:rounded-b-2xl ${
                          isSelected ? 'bg-primary-50/60' : 'active:bg-neutral-100'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleRowClick(org.id)}
                          aria-pressed={selectionMode ? isSelected : undefined}
                          className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-4 text-left focus-visible:outline-none"
                        >
                          {selectionMode && (
                            <span
                              aria-hidden="true"
                              className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                                isSelected ? 'bg-primary-600' : 'ring-1 ring-inset ring-neutral-300'
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={2.5} />}
                            </span>
                          )}
                          <OrgMark org={org} />
                          <span className="min-w-0 flex-1">
                            <span className="flex min-w-0 items-center gap-1.5 text-[15px] text-ink">
                              <span className="truncate">
                                <HighlightedText text={org.name} query={debouncedSearch} />
                              </span>
                              {org.endedOn && <DefunctPill />}
                            </span>
                            <span className="mt-0.5 block truncate text-[12px] text-neutral-400">
                              {typeColor && <Swatch color={typeColor} className="mr-1.5 align-middle" />}
                              {subtitle || 'No type'}
                            </span>
                          </span>
                          {org.status !== 'approved' && statusBadge(org.status)}
                        </button>
                        {selectionMode ? (
                          <span className="w-2.5" />
                        ) : rowMenuItems(org).length > 1 ? (
                          <Menu
                            items={rowMenuItems(org)}
                            trigger={(props) => (
                              <IconButton label={`Actions for ${org.name}`} className="h-10 w-10 text-neutral-400" {...props}>
                                <Ellipsis className="h-[18px] w-[18px]" strokeWidth={1.75} />
                              </IconButton>
                            )}
                          />
                        ) : (
                          <ChevronRight className="mx-2.5 h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </>
        )}

        {/* Tree view */}
        {!isTableView &&
          (hierarchyLoading ? (
            <Card divided>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex h-11 items-center gap-2.5 px-4">
                  <Skeleton className="h-6 w-6 rounded-lg" />
                  <Skeleton className="h-3.5 w-48" />
                </div>
              ))}
            </Card>
          ) : isEmpty ? (
            <Card>{emptyState}</Card>
          ) : (
            <HierarchicalOrganizationTable
              organizations={paginatedHierarchy}
              onOrganizationSelect={(id) => navigate(`/organizations/${id}`)}
              onOrganizationEdit={(id) => navigate(`/organizations/${id}/edit`)}
              onOrganizationDelete={(id) => setConfirmDeleteId(id)}
              onOrganizationView={(id) => navigate(`/organizations/${id}`)}
              deletingId={deletingId}
            />
          ))}

        {/* Pagination */}
        {!isLoading && !isEmpty && (
          <Pagination
            currentPage={page}
            totalItems={totalForPagination}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => { setLimit(newSize); setPage(1); }}
            pageSizeOptions={[10, 25, 50]}
            className="mt-3"
            bottomOffset={selectionMode && selectedIds.length > 0 ? SELECTION_BAR_CLEARANCE : undefined}
          />
        )}

        {/* Room for the selection action bar */}
        {selectionMode && selectedIds.length > 0 && <div className="h-20" />}
      </Page>

      {/* Selection action bar */}
      {selectionMode && selectedIds.length > 0 && (
        <SelectionActionBar
          selectedCount={selectedIds.length}
          maxSelection={2}
          onMerge={handleOpenMerge}
          onCancel={toggleSelectionMode}
          mergeLabel="Merge"
        />
      )}

      {/* Merge resolution sheet */}
      {mergeSheetOpen && mergeSource && mergeTarget && (
        <MergeResolutionSheet
          open={mergeSheetOpen}
          onClose={() => setMergeSheetOpen(false)}
          entityType="organization"
          sourceData={mergeSource as any}
          targetData={mergeTarget as any}
          sourceLabel={mergeSource.name}
          targetLabel={mergeTarget.name}
          fieldGroups={ORG_MERGE_FIELDS}
          onSwap={handleSwapMerge}
          onConfirmMerge={handleConfirmMerge}
          merging={merging}
          transferSummary="All stakeholder assignments will be transferred."
        />
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        open={!!confirmDeleteId}
        title="Delete organization"
        message={<span>This can't be undone.</span>}
        confirmText="Delete"
        loading={!!deletingId}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Error modal */}
      <MessageModal
        open={errorModal.open}
        title={errorModal.title}
        message={errorModal.message}
        type="error"
        onClose={() => setErrorModal({ open: false, title: '', message: '' })}
      />
    </DashboardLayout>
  );
};

/** A body that no longer exists: kept, with who held its posts */
const DefunctPill: React.FC = () => (
  <span
    className="flex-shrink-0 rounded-full bg-neutral-200/70 px-1.5 py-px text-[9.5px] uppercase tracking-[0.05em] text-neutral-500"
    title="This body no longer exists"
  >
    Defunct
  </span>
);
